import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthUser, LoginCredentials } from '../types';
import { apiService } from '../services/api';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: (redirectPath?: string) => void;
  setUser: (user: AuthUser) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  initializeAuth: () => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await apiService.login(credentials);

          const processedUser: AuthUser = {
            ...response.user,

            username: response.user.username || (response.user as any).name || 'Usuário',

            level: response.user.level || response.user.hierarchy?.level || 'user',
 
            permissions: response.user.group?.permissions || {},

            group: response.user.group || undefined,

          colors: response.user.colors || response.cms?.colors || {
            primary: '#3B82F6',
            secondary: '#1E40AF',
            accent: '#F59E0B',
            background: '#F8FAFC',
            text: '#1F2937'
          },
          cmsName: response.user.cmsName || response.cms?.name,
          cmsSlug: response.user.cmsSlug,
          logoUrl: response.user.logoUrl || response.cms?.logoUrl,

            credits: response.user.credits || 0
          };
          
          
          set({
            user: processedUser,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

      
      // Token gerado com sucesso - usuário será redirecionado pela UI
      return true;
        } catch (error: any) {
          set({
            error: error.message || 'Erro ao fazer login',
            isLoading: false,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      logout: async (redirectPath?: string) => {
        try {
          // Limpar estado primeiro para evitar requisições após logout
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: null,
          });
          
          // Fazer logout na API (vai cancelar requisições pendentes)
          await apiService.logout();
        } catch (error) {
          // Ignorar erros de logout (pode ser que o token já esteja expirado)
          console.warn('Aviso ao fazer logout (pode ser esperado):', error);
        } finally {
          // Garantir que o estado está limpo
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: null,
          });
          
          // Redirecionar apenas se path específico for fornecido
          // Não redirecionar para /login padrão para evitar valores padrão de slug
          if (redirectPath) {
            window.location.href = redirectPath;
          } else {
            // Redirecionar para raiz - deixar CmsSelector tratar
            window.location.href = '/';
          }
        }
      },


      setUser: (user: AuthUser) => {
        set({ user });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      initializeAuth: async () => {
        const getSlugFromPath = () => {
          try {
            const p = window.location.pathname || '';
            const seg = p.split('/').filter(Boolean);
            const maybe = seg[0] || '';
            if (!maybe) return null;
            const nonSlug = new Set(['login', 'download', 'api', 'static', 'assets']);
            if (nonSlug.has(maybe)) return null;
            return maybe;
          } catch (_) {
            return null;
          }
        };

        const isUserCacheValid = (u: any) => {
          const mg = Number(u?.member_group_id || 0);
          const hasGroup = !!u?.group;
          const hasPanel = typeof u?.panel === 'string' && u.panel.trim().length > 0;
          // Para liberar as rotas básicas, precisamos ao menos do member_group_id (hierarquia) e do painel/slug.
          // group é opcional (só impacta label de UI).
          return ([1, 2, 3, 4, 6].includes(mg) && hasPanel) || ([1, 2, 3, 4, 6].includes(mg) && hasGroup);
        };

        const resetToLogin = () => {
          try {
            localStorage.removeItem('auth-storage');
            localStorage.removeItem('_pv001');
            localStorage.removeItem('token');
            localStorage.removeItem('authToken');
            localStorage.removeItem('accessToken');
          } catch (_) {}
          set({ user: null, token: null, isAuthenticated: false, error: null });
          const slug = getSlugFromPath();
          window.location.href = slug ? `/${slug}/login` : '/';
        };

        // Verificar se há dados no auth-storage do Zustand
        const authStorage = localStorage.getItem('auth-storage');
        if (authStorage) {
          try {
            const authData = JSON.parse(authStorage);
            const { token, isAuthenticated } = authData.state || authData;
                        
            if (token && isAuthenticated) {
              // Verificar se o token ainda é válido
              try {
                // Timeout de 12 segundos para getProfile (cobre redes lentas/mobile)
                const profilePromise = apiService.getProfile();
                const timeoutPromise = new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Timeout ao validar token')), 12000)
                );
                
                const userProfile = await Promise.race([profilePromise, timeoutPromise]) as any;
                
                // Processar dados do usuário (backend já retorna no formato correto)
                const processedUser: AuthUser = {
                  id: userProfile.id,
                  username: userProfile.username,
                  email: userProfile.email,
                  name: userProfile.name,
                  level: userProfile.level || userProfile.role || 'user',
                  permissions: userProfile.permissions || {},
                  group: userProfile.group,
                  hierarchy: userProfile.hierarchy || {
                    isAdmin: userProfile.level === 'admin' || userProfile.role === 'admin',
                    isReseller: userProfile.level === 'reseller',
                    isMaster: userProfile.level === 'master',
                    isSubReseller: userProfile.level === 'subreseller',
                    isClient: userProfile.level === 'user',
                    level: userProfile.level || userProfile.role || 'user',
                    permissions: userProfile.level === 'admin' || userProfile.role === 'admin' ? ['all'] : []
                  },
                  // Campos obrigatórios da interface AuthUser
                  status: userProfile.status || 1,
                  panel: userProfile.panel || '', // Não usar valor padrão - cada revendedor tem seu slug
                  // Nunca assumir admin por default; em cache incompleto isso causa inconsistência de UI/permissões.
                  member_group_id: userProfile.member_group_id || 0,
                  owner_id: userProfile.owner_id || userProfile.id,
                  credits: userProfile.credits || 0,
                  // Preservar cores e dados do CMS
                  colors: userProfile.colors || {
                    primary: '#3B82F6',
                    secondary: '#1E40AF',
                    accent: '#F59E0B',
                    background: '#F8FAFC',
                    text: '#1F2937'
                  },
                  cmsName: userProfile.cmsName,
                  cmsSlug: userProfile.cmsSlug,
                  logoUrl: userProfile.logoUrl
                };
                
                set({ 
                  user: processedUser,
                  isAuthenticated: true,
                  token: token
                });
                
                return;
              } catch (error: any) {
                console.error('❌ Token inválido ou erro ao validar:', error);
                // Não fazer logout automático se for timeout ou erro de rede - pode ser problema de conectividade
                const isTimeout = error.message === 'Timeout ao validar token';
                const isNetworkError = error.code === 'ERR_NETWORK' || error.message?.includes('Network Error') || error.code === 'ECONNABORTED';
                
                if (isTimeout || isNetworkError) {
                  console.warn('⚠️ Timeout/rede ao validar token, mantendo sessão com dados do cache');
                  // Manter sessão local usando os dados já presentes no auth-storage
                  // O token existe no localStorage, então confiamos nele provisoriamente
                  const cachedUser = authData.state?.user || authData.user;
                  if (cachedUser && token && isUserCacheValid(cachedUser)) {
                    set({
                      user: cachedUser,
                      isAuthenticated: true,
                      token: token
                    });
                  } else {
                    // Cache incompleto em mobile costuma causar loop de 403; forçar relogin limpa a sessão quebrada.
                    resetToLogin();
                  }
                  return;
                }
                // Só derrubar sessão em erro real de autenticação.
                // 403 pode vir de bloqueio de ambiente/rede (WAF/proxy), então não deve forçar logout por padrão.
                const statusCode = error.response?.status;
                const rawErrorMessage = String(
                  error?.response?.data?.error ||
                  error?.response?.data?.message ||
                  error?.message ||
                  ''
                ).toLowerCase();
                const hasTokenSignal =
                  rawErrorMessage.includes('token') ||
                  rawErrorMessage.includes('jwt') ||
                  rawErrorMessage.includes('unauthorized') ||
                  rawErrorMessage.includes('não autenticado') ||
                  rawErrorMessage.includes('nao autenticado') ||
                  rawErrorMessage.includes('sessão expirada') ||
                  rawErrorMessage.includes('sessao expirada');
                const isAuthError = statusCode === 401 || (statusCode === 403 && hasTokenSignal);
                if (isAuthError) {
                  try {
                    get().logout();
                  } catch (logoutError) {
                    localStorage.removeItem('auth-storage');
                    set({ user: null, token: null, isAuthenticated: false });
                  }
                } else {
                  // Outros erros (500, etc): manter sessão do cache para não travar usuário
                  const cachedUser = authData.state?.user || authData.user;
                  if (cachedUser && token && isUserCacheValid(cachedUser)) {
                    console.warn('⚠️ Erro de servidor ao validar token, usando cache local');
                    set({
                      user: cachedUser,
                      isAuthenticated: true,
                      token: token
                    });
                  } else {
                    resetToLogin();
                  }
                }
                return;
              }
            }
          } catch (error) {
            console.error('❌ Erro ao parsear auth-storage:', error);
            localStorage.removeItem('auth-storage');
          }
        }
        
        // Fallback: verificar formato antigo _pv001
        const oldStorage = localStorage.getItem('_pv001');
        if (oldStorage) {
          try {
            const obj = JSON.parse(oldStorage);
            if (obj.t && obj.l) {
              try {
                const user = await apiService.getProfile();
                
                const processedUser: AuthUser = {
                  ...user,
                  level: user.level || user.hierarchy?.level || 'user',
                  permissions: user.group?.permissions || {},
                  group: user.group || undefined,
                  // Preservar cores e dados do CMS
                  colors: user.colors || {
                    primary: '#3B82F6',
                    secondary: '#1E40AF',
                    accent: '#F59E0B',
                    background: '#F8FAFC',
                    text: '#1F2937'
                  },
                  cmsName: user.cmsName,
                  cmsSlug: user.cmsSlug,
                  logoUrl: user.logoUrl,
                  // Garantir que créditos estejam disponíveis
                  credits: user.credits || 0
                };
                
                set({ 
                  user: processedUser,
                  isAuthenticated: true,
                  token: obj.t
                });
                
              } catch (error: any) {
                console.error('❌ Erro ao carregar perfil com token antigo:', error);
                const statusCode = error?.response?.status;
                const rawErrorMessage = String(
                  error?.response?.data?.error ||
                  error?.response?.data?.message ||
                  error?.message ||
                  ''
                ).toLowerCase();
                const hasTokenSignal =
                  rawErrorMessage.includes('token') ||
                  rawErrorMessage.includes('jwt') ||
                  rawErrorMessage.includes('unauthorized') ||
                  rawErrorMessage.includes('não autenticado') ||
                  rawErrorMessage.includes('nao autenticado') ||
                  rawErrorMessage.includes('sessão expirada') ||
                  rawErrorMessage.includes('sessao expirada');
                const isAuthError = statusCode === 401 || (statusCode === 403 && hasTokenSignal);

                if (isAuthError) {
                  localStorage.removeItem('_pv001');
                  get().logout();
                } else if (obj?.t) {
                  // Em erro de ambiente (ex.: 403 por rede/WAF), mantém sessão local para não derrubar usuário.
                  const existingUser = get().user;
                  if (existingUser && isUserCacheValid(existingUser)) {
                    set({
                      user: existingUser,
                      isAuthenticated: true,
                      token: obj.t
                    });
                  } else {
                    resetToLogin();
                  }
                }
              }
            }
          } catch (error) {
            console.error('❌ Erro ao parsear _pv001:', error);
            localStorage.removeItem('_pv001');
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
